import styled from '@emotion/styled/macro';
import RichText from '@components/rich-text/rich-text';
import vars from '@variables';
import StyledAccordion from '@components/accordion/accordion';
import {
  OrderedListAtom,
  UnorderedListAtom,
} from '@atoms/list-atoms/list-atoms';
import {
  H2Atom,
  H3Atom,
  HighlightAtom,
  HighlightAuthorAtom,
  PAtom,
} from '@atoms/text-atoms/text-atoms';
import { defineResponsiveGridColumns } from '@utils/utils';
import { ImageBlockContainer } from '@blocks/image-block/image-block.styles';
import { FlexibleLinkAtom } from '@atoms/link-atoms/link-atoms';
import {
  ArrowDownIconAtom,
  ArrowUpIconAtom,
} from '@atoms/icon-atoms/icon-atoms';
import { Title } from '@blocks/aligned-image-block/aligned-images-block.styles';
import { TooltipText } from '@atoms/glossary-tooltip-atoms/glossary-tooltip-atoms';
import { FlipcardContainer } from '@components/flip-card/flip-card.styles';
import { DottedLine } from '@blocks/highlight-block/highlight-block.styles';

// This style requires it's parent to have a position: relative to work properly
const expandingBackgroundStyles = `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
`;

export const BackgroundColor = styled.div<{ backgroundColor: string }>`
  ${expandingBackgroundStyles}
  background-color: ${({ backgroundColor }) => backgroundColor};

  &.hide-mobile {
    @media screen and (max-width: ${vars.breakPointTablet}) {
      display: none;
    }
  }

  &.hide-desktop {
    @media screen and (min-width: ${vars.breakPointTablet}) {
      display: none;
    }
  }
`;

export const BackgroundImage = styled.div`
  ${expandingBackgroundStyles}

  &.desktop-only {
    display: none;
    @media screen and (min-width: ${vars.breakPointTablet}) {
      display: block;
    }
  }

  &.mobile-only {
    display: block;
    @media screen and (min-width: ${vars.breakPointTablet}) {
      display: none;
    }
  }

  .gatsby-image-wrapper {
    height: 100%;
    width: 100%;

    img {
      object-fit: cover !important;
      width: 100% !important;
      height: 100% !important;
    }
  }
`;

export const GifOrSvgImage = styled.img`
  object-fit: cover;
  height: 100%;
  width: 100%;
`;

export const RichTextWrapper = styled.div<{
  anchorId?: string;
  applyPadding: boolean;
}>`
  position: relative;
  z-index: 2;
  margin-top: ${({ anchorId }) => (anchorId ? vars.spacingL : 'auto')};
  padding: ${({ applyPadding }) => (applyPadding ? `${vars.spacingL} 0` : '0')};

  @media screen and (min-width: ${vars.breakPointTablet}) {
    padding: ${({ applyPadding }) =>
      applyPadding ? `${vars.spacingXl} 0` : '0'};
  }

  .section-container {
    margin-bottom: 2rem;
  }

  > ${RichText} {
    > .grid-wrapper {
      display: grid;
      column-gap: ${vars.mobileColumnPercentageGap};
      grid-template-columns: ${defineResponsiveGridColumns(4)};

      @media screen and (min-width: ${vars.breakPointTablet}) {
        column-gap: ${vars.standardColumnPercentageGap};
        grid-template-columns: ${defineResponsiveGridColumns(8)};
      }

      @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
        column-gap: ${vars.standardColumnPercentageGap};
        grid-template-columns: ${defineResponsiveGridColumns(12)};
      }

      // For handling the old use case where text wrapped around the floated images.
      > img {
        width: 100%;
        border-radius: 8px;
      }

      > ${PAtom},
        > ${H2Atom},
        > ${H3Atom},
        > ${UnorderedListAtom},
        > ${OrderedListAtom},
        > img,
      blockquote {
        grid-column: col1-start/col4-end;

        @media screen and (min-width: ${vars.breakPointTablet}) {
          grid-column: col2-start/col7-end;
        }

        @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
          grid-column: col3-start/col10-end;
        }
      }

      ${UnorderedListAtom},
      ${OrderedListAtom} {
        margin-top: 1.6rem;
      }

      ${H2Atom},
      ${H3Atom} {
        margin-top: 2.4rem;
      }
    }
  }
`;

export const Container = styled.div<{
  colour: string;
  backgroundColor: string;
  hasDesktopBackgroundImage: boolean;
  hasMobileBackgroundImage: boolean;
}>`
  position: relative;
  width: 100%;
  color: ${({ colour }) => colour};
  li::marker {
    color: ${({ colour }) => colour};
  }

  ${RichTextWrapper} {
    color: ${({ colour }) => colour};
    > ${H2Atom} {
      color: ${({ colour }) => colour};
    }

    hr {
      width: 100%;
      margin: 24px auto;

      @media screen and (min-width: ${vars.breakPointTablet}) {
        width: 75%;
      }

      @media screen and (min-width: ${vars.breakPointSmallDesktop}) {
        width: 66.67%;
      }
    }

    ${DottedLine} {
      width: 100%;
      margin: 24px auto;
    }

    ${PAtom},
    ${H2Atom},
    ${H3Atom},
    ${HighlightAtom},
    ${HighlightAuthorAtom},
    ${UnorderedListAtom},
    ${OrderedListAtom},
    ${ImageBlockContainer},
    ${Title},
    img,
    span,
    blockquote,
    hr {
      color: ${({ colour }) => colour};
      background-color: ${({ hasMobileBackgroundImage, backgroundColor }) =>
        hasMobileBackgroundImage ? 'transparent' : backgroundColor};
      border-color: ${({ colour }) => colour};

      @media screen and (min-width: ${vars.breakPointTablet}) {
        background-color: ${({ hasDesktopBackgroundImage, backgroundColor }) =>
          hasDesktopBackgroundImage ? 'transparent' : backgroundColor};
      }
    }

    ${FlipcardContainer} {
      ${PAtom},
      ${H2Atom},
      ${H3Atom},
      ${HighlightAtom},
      ${HighlightAuthorAtom},
      ${UnorderedListAtom},
      ${OrderedListAtom},
      ${ImageBlockContainer},
      ${FlexibleLinkAtom},
      ${Title},
      img,
      span,
      blockquote {
        color: inherit;
        background-color: inherit;
      }
    }

    // styles related to accordion
    ${ArrowDownIconAtom} > path, ${ArrowUpIconAtom} > path {
      fill: ${({ colour }) => colour};
    }

    ${PAtom} > ${FlexibleLinkAtom} {
      color: ${({ colour }) => colour};
    }

    ${StyledAccordion} {
      border-top: 1px solid ${({ colour }) => colour};
    }

    ${TooltipText} {
      border-bottom: 1px dashed ${({ colour }) => colour};
    }
  }
`;
